import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { Observable, throwError } from 'rxjs';
import { filter, first, map, mergeMap } from 'rxjs/operators';
import * as readerConfigQuery from '../state/configuration/configuration.selectors';
import { LtiParams } from 'src/app/models/player-api.model';

export interface EpubFactoryPublishJson {
  version: string
  publish_timestamp: string
  epub_url?: string
  [key: string]: any
}

@Injectable({ providedIn: 'root' })
export class EpubFactoryApiService {
  private readonly store = inject(Store);
  private readonly http = inject(HttpClient);

  private readonly epubFactoryBaseUrl$ = this.store.pipe(
    select(readerConfigQuery.getEpubFactoryApi),
    filter((url: string | undefined): url is string => typeof url === 'string' && url.length > 0),
    first(),
  );

  private readonly epubFactoryCDNBaseUrl$ = this.store.pipe(
    select(readerConfigQuery.getEpubFactoryCDN),
    filter((url: string | undefined): url is string => typeof url === 'string' && url.length > 0),
    first(),
  );

  /**
   * Fetch the publish.json file from epub-factory using the provided access token
   * @param contextId The context ID (epub identifier)
   * @param accessToken The custom_epub_factory_access_token from LTI launch
   * @returns Observable of the publish.json content
   */
  fetchPublishJson(contextId: string, accessToken: string,
    version?: string, release?: string): Observable<EpubFactoryPublishJson> {
    return this.epubFactoryBaseUrl$.pipe(
      mergeMap((baseUrl) => {
        const url =
          version && release
            ? `${baseUrl}/publish-info/${contextId}/releases/${release}/versions/${version}.json`
            : `${baseUrl}/publish-info/${contextId}/latest.json`;
        const headers = new HttpHeaders({
          Authorization: `Bearer ${accessToken}`,
        });

        return this.http.get<EpubFactoryPublishJson>(url, {
          headers,
          withCredentials: true,
        });
      }),
    );
  }

  /**
   * Calculate the epub launch URL from the publish.json data
   * @param publishJson The publish.json response
   * @returns Observable with the calculated epub URL
   */
  calculateEpubLaunchUrl(publishJson: EpubFactoryPublishJson, lti: LtiParams): Observable<string> {
    const profile = lti?.custom_profile_resolution ?? '1080mp4';

    // Otherwise, construct the URL based on the publish data
    // This is a typical pattern for epub-factory URLs
    const files = Array.isArray(publishJson.files) ? publishJson.files : [];

    if (!files.length) {
      return throwError(
        () =>
          new Error(
            // eslint-disable-next-line max-len
            'EpubFactoryApiService: publish.json does not contain a valid "files" array for calculating the EPUB launch URL.',
          ),
      );
    }

    const file = files.find((f: any) => f.type === 'folder' && f.profile === profile);

    if (!file?.filePath) {
      return throwError(
        () =>
          new Error(
            // eslint-disable-next-line max-len
            `EpubFactoryApiService: No matching folder entry with profile "${profile}" found in publish.json "files" for calculating the EPUB launch URL.`,
          ),
      );
    }

    return this.epubFactoryCDNBaseUrl$.pipe(
      map((cdnBaseUrl) => {
        return `${cdnBaseUrl}/${file.filePath}/`;
      }),
    );
  }
}
